import { FC, useState } from 'react';
import { Avatar, Button, Col, Dropdown, Row, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  DownOutlined,
  HistoryOutlined,
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
  WalletOutlined
} from '@ant-design/icons';
import { useAuthDispatchContext, useAuthStateContext } from 'providers/Auth';
import { useUIDispatchContext } from 'providers/UI';
import { ReactComponent as Logo } from 'assets/img/logo.svg';
import { Link } from 'react-router-dom';
import { DOCTOR_HOW_IT_WORKS_PAGES } from '@medicusunion/front-utils';

import { getFileUrl } from 'utils/file';
import {
  StyledContactUs,
  StyledMainHeader,
  StyledMenuItem,
  StyledUserData,
  StyledUserDropdown
} from './styled';
import { Notifications } from '..';
import ContactUs from '../ContactUs';
import useIsMobile from 'hooks/useIsMobile';

const LANGUAGES = [
  {
    key: 'en',
    label: 'English'
  },
  {
    key: 'de',
    label: 'Deutsch'
  }
];

const MENU_ITEMS = [
  {
    title: 'profile_overview',
    route: '/my-profile/overview',
    icon: <UserOutlined />
  },
  {
    title: 'working_hours',
    route: '/my-profile/working-hours',
    icon: <HistoryOutlined />
  },
  {
    title: 'settings',
    route: '/my-profile/settings',
    icon: <SettingOutlined />
  },
  {
    title: 'payment_info',
    route: '/my-profile/payment',
    icon: <WalletOutlined />
  },
  {
    title: 'how_it_works',
    route: `/how-it-works/${DOCTOR_HOW_IT_WORKS_PAGES.DASHBOARD.key}`,
    icon: <UserOutlined />
  }
];

const Header: FC = () => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [isOpened, setOpened] = useState(false);
  const { toggleModal } = useUIDispatchContext();
  const { signOut } = useAuthDispatchContext();
  const { user } = useAuthStateContext();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onClickMenu = (info: any) => {
    switch (info?.key) {
      case 'logOut':
        toggleModal('confirm', true, {
          onConfirm: signOut
        });
        break;
      default:
        break;
    }
  };

  const handleClickLang = (info: { key: string }) => {
    i18n.changeLanguage(info.key);
    localStorage.setItem('locale', info.key);
  };

  const activeLang = LANGUAGES.find(item => item.key === i18n.language);

  return (
    <StyledMainHeader>
      <Row justify="space-between" align="middle" style={{ height: '100%' }}>
        <Col style={{ lineHeight: 1 }}>
          <Link to="/">
            <Logo width={150} />
          </Link>
        </Col>
        <Col>
          <Space size={isMobile ? 4 : 24} align="center">
            <Notifications />
            <Dropdown
              menu={{ items: LANGUAGES, onClick: handleClickLang }}
              placement="bottom"
              trigger={['hover']}
            >
              <Button type="text" style={{ padding: 0, color: 'white' }}>
                {activeLang?.label}
                <DownOutlined style={{ color: '#ffffff' }} />
              </Button>
            </Dropdown>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'user-item',
                    className: 'user-dropdown-data',
                    label: (
                      <StyledUserData>
                        <Avatar size={54} src={getFileUrl(user?.avatar)}>
                          {user?.first_name?.[0]}
                        </Avatar>
                        <span>
                          {user?.first_name} {user?.last_name}
                        </span>
                      </StyledUserData>
                    )
                  },
                  ...MENU_ITEMS.map(item => ({
                    key: item.route,
                    label: (
                      <Link to={item.route}>
                        <StyledMenuItem>
                          {item.icon}
                          <span>{t(`header.${item.title}`)}</span>
                        </StyledMenuItem>
                      </Link>
                    )
                  })),
                  {
                    key: 'logOut',
                    label: (
                      <StyledMenuItem>
                        <LogoutOutlined />
                        <span>{t('header.logout')}</span>
                      </StyledMenuItem>
                    )
                  },
                  {
                    key: 'contact-us',
                    className: 'user-dropdown-contact-us',
                    label: (
                      <StyledContactUs>
                        <ContactUs />
                      </StyledContactUs>
                    )
                  }
                ],
                onClick: onClickMenu
              }}
              placement="bottomRight"
              trigger={['hover']}
              overlayClassName="user-dropdown"
              onOpenChange={v => setOpened(v)}
              arrow
            >
              <StyledUserDropdown $active={isOpened}>
                <Avatar size={26} src={getFileUrl(user?.avatar)}>
                  {user?.first_name?.[0]}
                </Avatar>
                <p className="user-name">
                  {user?.first_name} {user?.last_name}
                </p>
                <DownOutlined
                  style={{
                    color: isOpened ? '#0B1A30' : '#ffffff',
                    fontSize: 10
                  }}
                />
              </StyledUserDropdown>
            </Dropdown>
          </Space>
        </Col>
      </Row>
    </StyledMainHeader>
  );
};

export default Header;
